/**
 * Evolution logic — pure + deterministic. Resolves level/trade/item/friendship
 * triggers (incl. trade-with-held-item, e.g. Haunter->Gengar) and produces the
 * updated instance, recomputing stats via core/stats. No rendering/IO.
 *
 * CLAUDE.md signature mechanic #4: trade-evolutions are wired end-to-end.
 * Voice never mutates canon; this is the canon-mutating step the caller invokes
 * only after the player chooses to evolve (a cancel leaves the mon untouched).
 */
import type {
  PokemonInstance,
  SpeciesData,
  EvolutionEntry,
  EvolutionTrigger,
  EvolutionEvent,
} from '@/shared';
import { computeStats, getNature } from './stats.js';

/** Extra runtime qualifiers a caller passes in (time-of-day, used item, cancel…). */
export type EvolutionContext = Readonly<Record<string, string | number | boolean>>;

/** A matched, ready-to-apply evolution: the target species + the canon event. */
export interface EvolutionMatch {
  readonly entry: EvolutionEntry;
  readonly toSpeciesId: string;
  readonly event: EvolutionEvent;
}

function conditionsMet(entry: EvolutionEntry, context: EvolutionContext): boolean {
  if (!entry.conditions) return true;
  for (const [key, expected] of Object.entries(entry.conditions)) {
    if (context[key] !== expected) return false;
  }
  return true;
}

function entryQualifies(
  mon: PokemonInstance,
  entry: EvolutionEntry,
  trigger: EvolutionTrigger,
  context: EvolutionContext,
): boolean {
  if (entry.trigger !== trigger) return false;
  if (!conditionsMet(entry, context)) return false;

  switch (trigger) {
    case 'level-up':
      if (entry.minLevel !== undefined && mon.level < entry.minLevel) return false;
      if (entry.minFriendship !== undefined && mon.friendship < entry.minFriendship) return false;
      return true;

    case 'item':
      // The stone is supplied by the caller via context.itemId.
      return entry.itemId !== undefined && context.itemId === entry.itemId;

    case 'trade':
      // Trade-with-held-item evos require the mon to be holding the right item.
      if (entry.heldItemId !== undefined && mon.heldItemId !== entry.heldItemId) return false;
      return true;

    case 'friendship':
      if (entry.minFriendship !== undefined && mon.friendship < entry.minFriendship) return false;
      if (entry.minLevel !== undefined && mon.level < entry.minLevel) return false;
      return true;

    default:
      return false;
  }
}

/**
 * Return the evolution this mon currently qualifies for under `trigger`, or null
 * ("not yet" / no branch / player cancelled). Read-only: never mutates the mon.
 *
 * Player-cancel path: pass `{ cancel: true }` in context to short-circuit to null,
 * so the caller's "ask the player" flow can decline without applying anything.
 */
export function checkEvolution(
  mon: PokemonInstance,
  species: SpeciesData,
  trigger: EvolutionTrigger,
  context: EvolutionContext = {},
): EvolutionMatch | null {
  if (context.cancel === true) return null;

  const entry = species.evolutions.find((e) => entryQualifies(mon, e, trigger, context));
  if (!entry) return null;

  return {
    entry,
    toSpeciesId: entry.toSpeciesId,
    event: {
      kind: 'evolution',
      instanceId: mon.id,
      fromSpeciesId: species.id,
      toSpeciesId: entry.toSpeciesId,
    },
  };
}

/**
 * Apply an evolution: return a NEW instance of the evolved species. Preserves
 * identity (id/IVs/EVs/nickname/moves/friendship/nature/origin…), retargets the
 * species, and recomputes max HP via core/stats. currentHp grows by the maxHp
 * delta so evolving never harms (and never over-heals) the mon. Held item and
 * the rest of the mutable state carry over; the caller consumes any used stone.
 */
export function evolveInstance(mon: PokemonInstance, toSpecies: SpeciesData): PokemonInstance {
  const nature = getNature(mon.natureId);
  const stats = computeStats(toSpecies.baseStats, mon.ivs, mon.evs, mon.level, nature);

  const maxHpDelta = stats.hp - mon.maxHp;
  const currentHp = Math.min(stats.hp, Math.max(0, mon.currentHp + maxHpDelta));

  return {
    ...mon,
    speciesId: toSpecies.id,
    maxHp: stats.hp,
    currentHp,
  };
}
